import AddOutlinedIcon from "@mui/icons-material/AddOutlined"
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined"
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined"
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import IconButton from "@mui/material/IconButton"
import Menu from "@mui/material/Menu"
import MenuItem from "@mui/material/MenuItem"
import Paper from "@mui/material/Paper"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useCallback, useEffect, useRef, useState } from "react"

import { parseApiErrorMessage } from "../../../common/apiErrors"
import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import LengthLimitedTextField from "../../../common/components/LengthLimitedTextField"
import Row from "../../../common/components/Row"
import DialogHeader from "../components/DialogHeader"
import InboxTemplateAIGenDialog from "../components/InboxTemplateAIGenDialog"
import InboxTemplateStatusChip from "../components/InboxTemplateStatusChip"
import {
  INBOX_TEMPLATE_AI_REVIEW_NOTE,
  INBOX_TEMPLATE_AI_REVIEW_READY_NOTE,
  improveSmsInboxTemplate,
} from "../components/inboxTemplateAi"
import { AUTOCOMPLETE_OPTIONS, useDialogContainer } from "../utils"
import { canEditResource } from "../utils/resourceAccess"

const TEMPLATES_URL = "/crm/api/concierge-sms/inbox/templates/"

export default function SmsInboxTemplatesDrawer({ open, onClose }) {
  const axios = useAuthorizedAxiosClient()
  const Container = useDialogContainer()
  const [templates, setTemplates] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [editing, setEditing] = useState(null)
  const [creating, setCreating] = useState(false)
  const [listError, setListError] = useState("")

  const loadTemplates = useCallback(() => {
    setIsLoading(true)
    return axios
      .get(TEMPLATES_URL, { params: { scope: "account" } })
      .then(({ data }) => {
        setTemplates(Array.isArray(data) ? data : data.results || [])
      })
      .catch(() => setTemplates([]))
      .finally(() => setIsLoading(false))
  }, [axios])

  useEffect(() => {
    if (!open) {
      setCreating(false)
      setEditing(null)
      setListError("")
      return undefined
    }
    loadTemplates()
    return undefined
  }, [open, loadTemplates])

  function handleSoftClose() {
    if (creating || editing) {
      setCreating(false)
      setEditing(null)
      return
    }
    onClose()
  }

  const showForm = creating || Boolean(editing)

  return (
    <Container
      open={open}
      onClose={() => onClose()}
      anchor="right"
      PaperProps={{
        sx: { width: { xs: "100%", sm: 560 }, maxWidth: "100%" },
      }}
    >
      <DialogHeader
        title={
          creating
            ? "New SMS template"
            : editing
            ? "Edit SMS template"
            : "SMS reply templates"
        }
        onClose={handleSoftClose}
      />
      <DialogContent>
        {showForm ? (
          <SmsInboxTemplateForm
            template={editing}
            onCancel={() => {
              setCreating(false)
              setEditing(null)
            }}
            onSaved={() => {
              setCreating(false)
              setEditing(null)
              loadTemplates()
            }}
            onError={setListError}
          />
        ) : (
          <Column sx={{ gap: 2 }}>
            {listError ? (
              <Alert severity="error" onClose={() => setListError("")}>
                {listError}
              </Alert>
            ) : null}
            <Typography variant="body2" sx={{ color: "colors.gray600" }}>
              These templates belong to this account and are only used when
              replying to inbound SMS. Run AI review, verify the updated
              content, then save. Templates are available immediately after
              save. Your account owner can reject or deactivate them later.
              Default templates from admin are also available in the reply
              picker and cannot be edited here.
            </Typography>
            <Row sx={{ justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                startIcon={<AddOutlinedIcon />}
                onClick={() => setCreating(true)}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Add template
              </Button>
            </Row>
            {isLoading ? (
              <Typography variant="body2" sx={{ color: "colors.gray500" }}>
                Loading templates…
              </Typography>
            ) : templates.length === 0 ? (
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  border: "1px dashed",
                  borderColor: "colors.gray200",
                  textAlign: "center",
                }}
              >
                <Typography variant="body2" sx={{ color: "colors.gray600" }}>
                  No SMS reply templates yet. Add one to reuse it when answering
                  inbound texts.
                </Typography>
              </Paper>
            ) : (
              templates.map((template) => (
                <TemplateListItem
                  key={template.id}
                  template={template}
                  onEdit={() => setEditing(template)}
                  onDeleted={loadTemplates}
                  onError={setListError}
                />
              ))
            )}
          </Column>
        )}
      </DialogContent>
    </Container>
  )
}

function TemplateListItem({ template, onEdit, onDeleted, onError }) {
  const axios = useAuthorizedAxiosClient()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  function handleDelete() {
    setIsDeleting(true)
    axios
      .delete(`${TEMPLATES_URL}${template.id}/`)
      .then(() => {
        setConfirmOpen(false)
        onDeleted()
      })
      .catch(({ response }) => {
        onError?.(
          parseApiErrorMessage(response?.data, "Could not delete SMS template")
        )
      })
      .finally(() => setIsDeleting(false))
  }

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: "1px solid",
          borderColor: "colors.gray200",
          borderRadius: 2,
        }}
      >
        <Row
          sx={{
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Column sx={{ minWidth: 0, gap: 0.5 }}>
            <Row sx={{ alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography fontWeight={700} color="text.primary">
                {template.name}
              </Typography>
              <InboxTemplateStatusChip status={template.approval_status} />
            </Row>
            <Typography variant="body2" sx={{ color: "colors.gray600" }} noWrap>
              {template.text || "No message"}
            </Typography>
          </Column>
          <Row sx={{ gap: 0.5 }}>
            {canEditResource(template) ? (
              <>
                <Button
                  size="small"
                  onClick={onEdit}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  Edit
                </Button>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => setConfirmOpen(true)}
                  aria-label="Delete template"
                >
                  <DeleteOutlineOutlinedIcon fontSize="small" />
                </IconButton>
              </>
            ) : (
              <Typography variant="caption" color="text.secondary">
                View only
              </Typography>
            )}
          </Row>
        </Row>
      </Paper>
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent>
          <Typography>
            Delete SMS template <strong>{template.name}</strong>? This only
            affects inbound SMS replies.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setConfirmOpen(false)}>
            Cancel
          </Button>
          <LoadingButton
            color="error"
            variant="contained"
            loading={isDeleting}
            onClick={handleDelete}
          >
            Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  )
}

function SmsInboxTemplateForm({ template, onCancel, onSaved, onError }) {
  const axios = useAuthorizedAxiosClient()
  const [name, setName] = useState(template?.name || "")
  const [text, setText] = useState(template?.text || "")
  const [isSaving, setIsSaving] = useState(false)
  const [isAiReviewing, setIsAiReviewing] = useState(false)
  const [saveError, setSaveError] = useState("")
  const [aiReviewToken, setAiReviewToken] = useState("")
  const [aiReviewedDraft, setAiReviewedDraft] = useState(null)
  const [aiPrompt, setAiPrompt] = useState("")
  const [aiGenDialogOpen, setAiGenDialogOpen] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState(null)
  const textRef = useRef(null)

  function invalidateAiReview() {
    setAiReviewToken("")
    setAiReviewedDraft(null)
  }

  function insertPlaceholder(fieldName) {
    const token = `{{ ${fieldName} }}`
    const input = textRef.current
    const start = input?.selectionStart ?? text.length
    const end = input?.selectionEnd ?? text.length
    const next = `${text.slice(0, start)}${token}${text.slice(end)}`
    setText(next)
    invalidateAiReview()
    setMenuAnchor(null)
  }

  function applyReviewedDraft(reviewedDraft, token, prompt = "") {
    const nextDraft = {
      name: reviewedDraft.name || "",
      text: reviewedDraft.text || "",
    }
    setName(nextDraft.name)
    setText(nextDraft.text)
    setAiReviewToken(token)
    setAiReviewedDraft(nextDraft)
    setAiPrompt(prompt)
  }

  function handleAiApply({
    draft: reviewedDraft,
    aiReviewToken,
    aiPrompt: prompt,
  }) {
    applyReviewedDraft(reviewedDraft, aiReviewToken, prompt || "")
  }

  const isReadyToSave = Boolean(aiReviewToken && aiReviewedDraft)

  async function handleAiReview() {
    if (!name.trim() || !text.trim()) return

    setSaveError("")
    setIsAiReviewing(true)
    try {
      const reviewed = await improveSmsInboxTemplate(axios, {
        name: name.trim(),
        text: text.trim(),
      })
      applyReviewedDraft(
        {
          name: reviewed.name,
          text: reviewed.text,
        },
        reviewed.aiReviewToken,
        ""
      )
    } catch ({ response }) {
      const message = parseApiErrorMessage(
        response?.data,
        "Couldn't review that template. Please try again."
      )
      setSaveError(message)
      onError?.(message)
    } finally {
      setIsAiReviewing(false)
    }
  }

  function handleSave() {
    if (!isReadyToSave) return

    setSaveError("")
    const payload = {
      name: aiReviewedDraft.name,
      text: aiReviewedDraft.text,
      ai_review_token: aiReviewToken,
      ai_prompt: aiPrompt,
    }
    setIsSaving(true)
    const request = template
      ? axios.put(`${TEMPLATES_URL}${template.id}/`, payload)
      : axios.post(TEMPLATES_URL, payload)
    request
      .then(() => onSaved())
      .catch(({ response }) => {
        const message = parseApiErrorMessage(
          response?.data,
          "Could not save SMS template"
        )
        setSaveError(message)
        onError?.(message)
        invalidateAiReview()
      })
      .finally(() => setIsSaving(false))
  }

  const canReview = Boolean(name.trim()) && Boolean(text.trim())
  const canSave = isReadyToSave

  return (
    <Column sx={{ gap: 2 }}>
      <Alert severity="info">
        {isReadyToSave
          ? INBOX_TEMPLATE_AI_REVIEW_READY_NOTE
          : INBOX_TEMPLATE_AI_REVIEW_NOTE}
      </Alert>
      {saveError ? (
        <Alert severity="error" onClose={() => setSaveError("")}>
          {saveError}
        </Alert>
      ) : null}
      <Row
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Button
          startIcon={<ArrowBackOutlinedIcon />}
          onClick={onCancel}
          sx={{ textTransform: "none" }}
        >
          Back to templates
        </Button>
        {!template ? (
          <Button
            variant="outlined"
            startIcon={<AutoAwesomeOutlinedIcon />}
            onClick={() => setAiGenDialogOpen(true)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            AI generate
          </Button>
        ) : null}
      </Row>
      <LengthLimitedTextField
        label="Template name"
        value={name}
        setValue={(value) => {
          invalidateAiReview()
          setName(value)
        }}
        maxLength={128}
        required
      />
      <TextField
        inputRef={textRef}
        label="Message"
        value={text}
        onChange={(e) => {
          invalidateAiReview()
          setText(e.target.value)
        }}
        multiline
        minRows={6}
        required
        fullWidth
      />
      <Row>
        <Button
          size="small"
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          sx={{ textTransform: "none" }}
        >
          Insert placeholder
        </Button>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
        >
          {AUTOCOMPLETE_OPTIONS.map((option) => (
            <MenuItem
              key={option.fieldName}
              onClick={() => insertPlaceholder(option.fieldName)}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>
      </Row>
      <Row sx={{ justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
        <Button variant="outlined" color="inherit" onClick={onCancel}>
          Cancel
        </Button>
        {!isReadyToSave ? (
          <LoadingButton
            variant="contained"
            loading={isAiReviewing}
            disabled={!canReview}
            startIcon={<AutoAwesomeOutlinedIcon />}
            onClick={handleAiReview}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {isAiReviewing ? "Reviewing with AI…" : "Review with AI"}
          </LoadingButton>
        ) : (
          <LoadingButton
            variant="contained"
            loading={isSaving}
            disabled={!canSave}
            onClick={handleSave}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {template ? "Save template" : "Add template"}
          </LoadingButton>
        )}
      </Row>
      <InboxTemplateAIGenDialog
        open={aiGenDialogOpen}
        medium="sms"
        onApply={handleAiApply}
        onClose={() => setAiGenDialogOpen(false)}
      />
    </Column>
  )
}
